import React, { useState } from 'react'
import { t } from '@/components/common/Helpers'
import PhotoCarouselModal from './PhotoCarouselModal'
import Tilt from 'react-parallax-tilt'

export default function VintageGallery({
    obituary,
    showContainer = false,
    titleClasses = "app-title text-4xl mb-6 text-center text-gray-700",
    buttonClasses = 'shadow-sm border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 px-4 py-2 text-sm rounded-md inline-flex items-center justify-center font-medium focus:outline-none focus:ring-2 focus:ring-offset-2',
    fitContainer = false,
    isPreviewMode = false
}) {
    const [showFullGallery, setShowFullGallery] = useState(false)
    const [selectedImageIndex, setSelectedImageIndex] = useState(0)

    if (!obituary || !obituary.gallery_photos || obituary.gallery_photos.length === 0) {
        return null
    }

    const photos = obituary.gallery_photos.map((photo, index) => ({
        original: photo.xl,
        thumbnail: photo.md,
        originalAlt: `${obituary.display_name} photo ${index + 1} of ${obituary.gallery_photos.length}`,
        thumbnailAlt: `${obituary.display_name} photo thumbnail ${index + 1} of ${obituary.gallery_photos.length}`,
        thumbnailLoading: "lazy",
        loading: "lazy",
    }))
    const mainPhoto = photos[0]
    const surroundingPhotos = photos.slice(1, 9)

    const handleViewGallery = (imageIndex = 0) => {
        if (isPreviewMode) {
            return
        }
        setSelectedImageIndex(imageIndex)
        setShowFullGallery(true)
    }

    const handlePhotoClick = (photoIndex) => {
        if (isPreviewMode) {
            return
        }
        const actualIndex = photoIndex === 'main' ? 0 : photoIndex + 1
        handleViewGallery(actualIndex)
    }

    return (
        <>
            <div className={`relative w-full py-8 overflow-hidden ${fitContainer
                ? 'max-w-7xl mx-auto'
                : 'w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw]'
                }`}>
                <div className={`min-h-[400px] sm:min-h-[500px] lg:min-h-[600px] xl:min-h-[700px] ${fitContainer ? '' : 'max-w-7xl mx-auto relative'
                    }`}>
                    {showContainer &&
                        <div
                            className="absolute inset-0 pointer-events-none"
                            style={{
                                background: 'radial-gradient(ellipse at left, #C9DFE5 0%, rgba(201, 223, 229, 0.55) 1%, white 100%)'
                            }}
                        ></div>
                    }

                    {!isPreviewMode &&
                        <h2 className={`absolute top-[10%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${titleClasses}`}>
                            {t.trans('obituaries.photo-gallery-photos')}
                        </h2>
                    }

                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20" onClick={isPreviewMode ? () => { } : () => handlePhotoClick('main')}>
                        <Tilt className="relative overflow-hidden rounded-2xl shadow-2xl bg-white p-2 sm:p-3 transition-all duration-700 hover:shadow-3xl group cursor-pointer w-[200px] h-[267px] sm:w-[240px] sm:h-[320px] lg:w-[280px] lg:h-[373px] xl:w-[300px] xl:h-[407px]">
                            <div className="w-full h-full overflow-hidden rounded-xl bg-gradient-to-br from-gray-50 to-white">
                                <img
                                    src={mainPhoto.original}
                                    alt={`${obituary.display_name} main photo`}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 sepia-[0.1] contrast-[0.9] brightness-[0.95]"
                                    loading="lazy"
                                />
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </Tilt>
                    </div>

                    {/* Left Side Photos */}
                    {surroundingPhotos.slice(0, 4).map((photo, index) => {
                        const leftPositionClasses = [
                            'top-[25%] left-[3%] w-[80px] h-[107px] sm:top-[20%] sm:w-[100px] sm:h-[133px] lg:top-[22%] lg:left-[2%] lg:w-[110px] lg:h-[147px] xl:w-[119px] xl:h-[164px]',
                            'top-[60%] left-[3%] w-[90px] h-[120px] sm:top-[8%] sm:left-[15%] sm:w-[120px] sm:h-[160px] lg:top-[10%] lg:left-[16%] lg:w-[140px] lg:h-[187px] xl:w-[164px] xl:h-[219px]',
                            'hidden sm:block top-[55%] left-[18%] w-[100px] h-[133px] lg:top-[52%] lg:left-[20%] lg:w-[115px] lg:h-[153px] xl:left-[21%] xl:w-[124px] xl:h-[164px]',
                            'hidden sm:block top-[68%] left-[4%] w-[120px] h-[160px] lg:top-[60%] lg:left-[3.5%] lg:w-[140px] lg:h-[187px] xl:w-[164px] xl:h-[219px]',
                        ]

                        const positionClasses = leftPositionClasses[index]

                        return (
                            <div key={`left-${index}`} onClick={isPreviewMode ? () => { } : () => handlePhotoClick(index)}>
                                <Tilt className={`absolute overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 bg-white p-1 sm:p-2 group hover:z-30 cursor-pointer ${positionClasses}`}>
                                    <div className="w-full h-full overflow-hidden rounded-lg bg-gradient-to-br from-gray-50 to-white">
                                        <img
                                            src={photo.thumbnail}
                                            alt={`${obituary.display_name} photo ${index + 2}`}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 sepia-[0.15] contrast-[0.9] brightness-[0.95]"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                </Tilt>
                            </div>
                        )
                    })}

                    {/* Right Side Photos */}
                    {surroundingPhotos.slice(4, 8).map((photo, index) => {
                        const rightPositionClasses = [
                            'top-[35%] right-[3%] w-[80px] h-[107px] sm:top-[15%] sm:w-[120px] sm:h-[90px] lg:top-[12%] lg:right-[2%] lg:w-[140px] lg:h-[105px] xl:right-[1%] xl:w-[164px] xl:h-[116px]',
                            'top-[70%] right-[3%] w-[90px] h-[120px] sm:top-[28%] sm:right-[18%] sm:w-[120px] sm:h-[160px] lg:w-[140px] lg:h-[187px] xl:right-[18.5%] xl:w-[160px] xl:h-[215px]',
                            'hidden sm:block top-[72%] right-[20%] w-[100px] h-[133px] lg:top-[68%] lg:w-[115px] lg:h-[153px] xl:w-[124px] xl:h-[164px]',
                            'hidden sm:block top-[48%] right-[4%] w-[120px] h-[160px] lg:right-[3.5%] lg:w-[140px] lg:h-[187px] xl:right-[2%] xl:w-[164px] xl:h-[219px]',
                        ]

                        const positionClasses = rightPositionClasses[index]

                        return (
                            <div key={`right-${index}`} onClick={isPreviewMode ? () => { } : () => handlePhotoClick(index + 4)}>
                                <Tilt className={`absolute overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-500 bg-white p-1 sm:p-2 group hover:z-30 cursor-pointer ${positionClasses}`}>
                                    <div className="w-full h-full overflow-hidden rounded-lg bg-gradient-to-br from-gray-50 to-white">
                                        <img
                                            src={photo.thumbnail}
                                            alt={`${obituary.display_name} photo ${index + 6}`}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 sepia-[0.15] contrast-[0.9] brightness-[0.95]"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                </Tilt>
                            </div>
                        )
                    })}

                    {!isPreviewMode && (
                        <div className="absolute bottom-4 sm:bottom-8 md:bottom-4 xl:bottom-16  left-1/2 transform -translate-x-1/2 z-30">
                            <button
                                onClick={() => handleViewGallery(0)}
                                className={buttonClasses}
                            >
                                View All Photos
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <PhotoCarouselModal
                photos={photos}
                isOpen={showFullGallery}
                onClose={() => setShowFullGallery(false)}
                currentPhotoIndex={selectedImageIndex}
                setCurrentPhotoIndex={setSelectedImageIndex}
                showBackgroundBlur={true}
            />
        </>
    )
}
